<template>
  <b-container fluid v-if="!isLoading">
    <b-row>
      <b-col cols="12">
        <div class="d-flex align-items-center">
          <b-button @click="navigateBack" variant="light">
            <font-awesome-icon icon="arrow-left" size="2x" />
          </b-button>
          <div class="h3 mb-0 ml-3">Voter et signer la correspondance</div>
        </div>
      </b-col>
      <b-col>
        <hr />
        <h3 class="mt-3">{{ meeting.name }}</h3>
        <hr />
      </b-col>
    </b-row>
    <b-row v-if="!buildingOwner.email">
      <b-col cols="12">
        <div class="h5">
          Veuillez ajouter une adresse e-mail afin de voter et de signer la correspondance.
        </div>
      </b-col>
      <b-col cols="3" class="d-flex flex-column">
        <ValidationObserver>
          <div>
            <label for="emailInput">{{ "E-mail" }}</label>
            <ValidationProvider
              ref="vee-b-email"
              v-slot="{ errors, classes }"
              name="email"
              rules="required|email"
            >
              <b-input id="emailInput" v-model="emailToSubmit.email" />
              <span :class="classes">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>
          <div class="mt-3">
            <label for="emailInput">{{ "Vérification de l'email" }}</label>
            <ValidationProvider
              ref="vee-b-email-confirm"
              v-slot="{ errors, classes }"
              name="confirm"
              rules="required|email|confirmed:email"
            >
              <b-input id="emailInput" v-model="emailToSubmit.verifyEmail" />
              <span :class="classes">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>
        </ValidationObserver>
        <div class="mt-3">
          <b-button class="btn-blue" @click="updateBuildingOwner">Submit</b-button>
        </div>
      </b-col>
    </b-row>
    <b-row v-if="buildingOwner.email">
      <b-col cols="12">
        <div class="h5 mt-3">
          Vous pouvez voter ou modifier vos votes jusqu'au
          {{ this.$moment(meeting.last_reception_date).format("DD MMMM YYYY HH:mm") }}
        </div>
      </b-col>
      <b-col class="mt-4 mb-5" cols="12">
        <appVotingTable
          :current-mandate="mandate"
          :current-mandate-id="mandate.id"
          :building-id="meeting.building_id"
        />
      </b-col>
    </b-row>
    <b-row v-if="buildingOwner.email">
      <b-col v-if="docageSignDate" cols="auto">
        <div class="h6 mb-4 bg-success text-white p-2 rounded">
          Le document a été voté et signé le
          {{ this.$moment(docageSignDate).format("DD MMMM YYYY HH:mm") }}
        </div>
      </b-col>
      <b-col cols="12" class="mb-4 d-flex-column d-lg-flex justify-content-lg-between">
        <div class="d-flex">
          <b-button
            v-if="
              !lastReceptionDateExpired && (!docageSignDate || initialProposedVotesWhereChanged)
            "
            class="button btn-blue mr-2"
            @click="initVoteAndSignProcess"
          >
            <font-awesome-icon v-if="!isLoadingSave" class="agv-button-icon" icon="save" />
            <font-awesome-icon v-else class="agv-button-icon" icon="spinner" pulse />
            <span>{{ "Votez et signez la correspondance" }}</span>
          </b-button>

          <a
            v-if="correspondenceMedia"
            :href="PUBLIC_PATH + correspondenceMedia.file_path"
            target="_blank"
            class="button btn btn-secondary"
          >
            <font-awesome-icon icon="file-pdf" class="green" />
            <span class="ml-2 align-self-center">Voir le PDF de la correspondance</span>
          </a>
        </div>
        <div class="d-flex mt-3 mt-lg-0">
          <b-button @click="navigateBack" variant="secondary">
            <span class="h5">Annuler</span>
          </b-button>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>
<script>
import MeetingORM from "@/store/orm-axios/models/Meeting";
import MandateORM from "@/store/orm-axios/models/Mandate";
import MediaORM from "@/store/orm-axios/models/Media";
import MeetingDetailsMixin from "@/mixins/meeting-details";
import MeetingCorrespondenceVoteORM from "@/store/orm-axios/models/MeetingCorrespondenceVote";
import MeetingResolutionORM from "@/store/orm-axios/models/MeetingResolution";
import ResolutionVoteORM from "@/store/orm-axios/models/ResolutionVote";
import ConfigurationORM from "@/store/orm-axios/models/Configuration";
import MajorityORM from "@/store/orm-axios/models/Majority";
import BuildingOwnerORM from "@/store/orm-axios/models/BuildingOwner";
import UserORM from "@/store/orm-axios/models/User";
import DocageORM from "@/store/orm-axios/models/Docage";
import usePDFs from "@/use/usePDFs.js";
import { voteTypes } from "@/constants/vote.js";
import Swal from "sweetalert2/dist/sweetalert2.js";
import APILinks from "@/links/links.js";
import { mandatePresenceStatuses, correspondenceTypes } from "@/constants/mandate.js";
export default {
  components: {
    appVotingTable: () =>
      import("@/components/correspondence-vote-and-sign/CorrespondenceVotesTable"),
  },
  mixins: [MeetingDetailsMixin],
  data() {
    return {
      isLoading: true,
      isLoadingSave: false,
      pdfFooterMessage: "",
      pdfContactDetails: "",
      pdfAgencyEmail: "",
      agencyMediaId: null,
      emailToSubmit: {
        email: "",
        verifyEmail: "",
      },
      PUBLIC_PATH: "",
      initialProposedVotes: [],
      initialProposedVotesWhereChanged: false,
    };
  },
  computed: {
    agencyId() {
      return Number(localStorage.getItem("agencyId"));
    },
    meetingId() {
      return this.$route.params.id;
    },
    meeting() {
      return MeetingORM.find(this.meetingId);
    },
    mandate() {
      if (this.meeting) {
        return MandateORM.query().where("meeting_id", this.meeting.id).first();
      }
      return null;
    },
    meetingCorrespondenceVotes() {
      if (this.mandate) {
        return MeetingCorrespondenceVoteORM.query().where("mandate_id", this.mandate.id).first();
      }
      return {};
    },
    configuration() {
      // NOTE: There will be only one configuration for each agency id
      return ConfigurationORM.query().where("agency_id", this.agencyId).first();
    },
    majorities() {
      return MajorityORM.query().get();
    },
    buildingOwner() {
      return BuildingOwnerORM.find(this.mandate.building_owner_id);
    },
    correspondenceMedia() {
      return MediaORM.find(this.mandate.voting_form_id);
    },
    docageSignDate() {
      const docage = DocageORM.find(this.mandate.docage_id);
      return docage && docage.sign_date ? docage.sign_date : null;
    },
    lastReceptionDateExpired() {
      if (this.meeting && this.meeting.last_reception_date) {
        return this.$moment().isAfter(this.meeting.last_reception_date);
      } else {
        return false;
      }
    },
  },
  async created() {
    this.PUBLIC_PATH = APILinks.PUBLIC_PATH();

    await this.getMeetingDetails();

    await ConfigurationORM.api()
      .FETCH(this.agencyId)
      .then(() => {
        this.getPdfConfigs();
      });

    await UserORM.api()
      .GET_ADMIN_BY_AGENCY(this.agencyId)
      .then(res => {
        const agencyAdmins = res.response.data && res.response.data["hydra:member"];
        this.agencyMediaId = agencyAdmins[0].mediaId;
      });
  },
  watch: {
    meetingCorrespondenceVotes(newVal) {
      if (newVal && newVal.proposed_votes) {
        if (this.initialProposedVotes.length === 0) {
          this.initialProposedVotes = newVal.proposed_votes;
        } else {
          this.compareProposedVotes(newVal.proposed_votes);
        }
      }
    },
  },
  methods: {
    async getMeetingDetails() {
      this.$store.state.showLoaderOverlay = true;
      this.isLoading = true;
      await MeetingORM.api()
        .GET_DETAILS_FOR_CORRESPONDENCE_VOTING(this.meetingId)
        .then(async () => {
          await this.setMeetingDetails(this.meeting);
          this.isBusy = false;
          this.$store.state.showLoaderOverlay = false;
          this.isLoading = false;
        })
        .catch(err => {
          console.error(err);
          this.isBusy = false;
          this.$store.state.showLoaderOverlay = false;
          this.isLoading = false;
        });
    },
    getPdfConfigs() {
      // Get current pdf_configs value
      if (this.configuration && this.configuration.settings.length > 0) {
        this.configuration.settings.forEach(element => {
          if (element.pdf_footer) {
            this.pdfFooterMessage = element.pdf_footer;
          }
          if (element.pdf_contact_details) {
            this.pdfContactDetails = element.pdf_contact_details;
          }
          if (element.agency_email) {
            this.pdfAgencyEmail = element.agency_email;
          }
        });
      }
    },
    async initVoteAndSignProcess() {
      this.isLoadingSave = true;

      Swal.fire({
        icon: "success",
        title: `Vous recevrez dans quelques minutes un mail à ${this.buildingOwner.email} avec un lien où vous pourrez signer la correspondance.`,
        showConfirmButton: true,
        confirmButtonText: "OK",
        buttonsStyling: false,
        confirmButtonClass: "btn btn-success btn-lg",
      });

      // NOTE: Auto vote for not voted correspondence as Abstain
      this.setAbsOnNotVotedCorrespondence();
      this.createResolutionVotes();

      // NOTE: Get mandates with resolutionVotes (for api)
      let mandate = MandateORM.query()
        .where("id", this.mandate.id)
        .with("resolution_votes", query => {
          query.where(rv => {
            // !!!: ignore resolution votes with revote_majority, because are only for visualisation purpose
            return !rv.revote_majority;
          });
        })
        .first();

      mandate["presence_status"] = mandatePresenceStatuses.correspondence;
      mandate["voted_by_correspondence"] = correspondenceTypes.by_app;
      mandate["reception_date"] = this.$moment().utc().format("YYYY-MM-DDTHH:mm:ssZ");
      // NOTE: Set mandateId (for api)
      mandate["mandateId"] = mandate.id;
      delete mandate.id;

      await MandateORM.api()
        .MANDATE_CORRESPONDENCE_VOTING(mandate)
        .then(async res => {
          this.isLoadingSave = false;
          if (res.response.data && !res.response.data.success) {
            return Swal.fire({
              icon: "error",
              title: "Error",
            });
          }
          await this.callVoteAndSignApi();
        })
        .catch(err => {
          console.log("error happened", err);
          this.isLoadingSave = false;
          return Swal.fire({
            icon: "error",
            title: "Error",
          });
        });
    },
    setAbsOnNotVotedCorrespondence() {
      let proposedVotes = this.meetingCorrespondenceVotes.proposed_votes;
      if (proposedVotes && proposedVotes.length > 0) {
        proposedVotes.forEach(proposed_vote => {
          proposed_vote.vote = proposed_vote.vote == null ? voteTypes.abstain : proposed_vote.vote;
        });
        MeetingCorrespondenceVoteORM.update({
          where: this.meetingCorrespondenceVotes.id,
          data: {
            proposed_votes: proposedVotes,
          },
        });
      }
    },
    createResolutionVotes() {
      let meetingResolutions = MeetingResolutionORM.query()
        .where("meeting_id", this.meetingId)
        .hasNot("children_resolutions")
        .orderBy("position")
        .get();
      let proposedVotes = this.meetingCorrespondenceVotes.proposed_votes;
      if (proposedVotes && proposedVotes.length > 0) {
        meetingResolutions.forEach(meetingResolution => {
          let vote = this.$_.find(proposedVotes, item => {
            return (
              item.meeting_resolution_id == meetingResolution.id &&
              item.majority_id == meetingResolution.majority_id
            );
          });
          let existentVote = ResolutionVoteORM.getters("getVoteByMandateAndResolution")(
            this.mandate.id,
            meetingResolution.id
          );
          if (vote && !existentVote) {
            ResolutionVoteORM.insertOrUpdate({
              data: {
                meeting_has_resolution_id: meetingResolution.id,
                mandate_id: this.mandate.id,
                vote: null,
                presence_status: this.mandate.presence_status,
                agency_id: Number(localStorage.getItem("agencyId")),
              },
            });
          }
        });
      }
    },
    async generateCorrespondencePdf() {
      const { generateTemplateVBC } = usePDFs();

      let pdfToGenerate = {};

      let meeting = MeetingORM.query()
        .where("id", this.meeting.id)
        .with("building")
        .with("meeting_resolutions")
        .with("meeting_resolutions.majority")
        .with("meeting_resolutions.parent_resolution_id")
        .with("meeting_resolutions.voting_category")
        .with("meeting_resolutions.children_resolutions")
        .with("meeting_resolutions.children_resolutions.majority")
        .with("meeting_resolutions.children_resolutions.parent_resolution_id")
        .with("meeting_resolutions.children_resolutions.voting_category")
        .with("meeting_resolutions.children_resolutions.voting_category_id")
        .with("meeting_resolutions.children_resolutions.resolution_number")
        .first();

      const mandate = MandateORM.query()
        .where("id", this.mandate.id)
        .with("building")
        .with("building_owner.building_owner_building_lots.building_lot")
        .with(
          "building_owner.building_owner_building_lots.building_owner_voting_rights.voting_category"
        )
        .first();

      const votes = this.meetingCorrespondenceVotes.proposed_votes;

      await MediaORM.api()
        .GET_LOGO_IN_BASE64(this.agencyMediaId)
        .then(res => {
          meeting["logoInBase64"] = res.response.data
            ? res.response.data["file_as_base64_string"]
            : null;
        })
        .catch(err => {
          console.error(err);
          Swal.fire({
            icon: "error",
            title: "Impossible d'extraire le logo !",
            text: "Veuillez vérifier votre connexion Internet. !",
          });
        });

      await generateTemplateVBC(
        meeting,
        mandate,
        this.pdfFooterMessage,
        this.majorities,
        this.pdfContactDetails,
        this.pdfAgencyEmail,
        votes
      ).then(template => {
        pdfToGenerate.correspondence = template;
      });

      await MediaORM.api()
        .MANDATE_PDF_UPDATE_CORRESPONDENCE(pdfToGenerate, this.mandate.id)
        .catch(err => {
          this.generatePdfCommonError(err);
        });
    },
    async callVoteAndSignApi() {
      try {
        await MeetingCorrespondenceVoteORM.api().PUT(this.meetingCorrespondenceVotes);
        await this.generateCorrespondencePdf();
        await MandateORM.api().MANDATE_SIGN_CORRESPONDENCE(this.mandate.id);
      } catch (error) {
        console.log("error happened", err);
        this.isLoadingSave = false;
        return Swal.fire({
          icon: "error",
        });
      }
    },
    async updateBuildingOwner() {
      let isEmailValid = true;
      await this.$refs["vee-b-email"].validate().then(res => {
        if (!res.valid) {
          isEmailValid = false;
        }
      });
      let isEmailConfirmValid = true;
      await this.$refs["vee-b-email-confirm"].validate().then(res => {
        if (!res.valid) {
          isEmailConfirmValid = false;
        }
      });

      if (isEmailValid && isEmailConfirmValid) {
        this.$store.state.showLoaderOverlay = true;
        await BuildingOwnerORM.api()
          .PUT({
            ...this.buildingOwner,
            email: this.emailToSubmit.email,
          })
          .then(() => {
            BuildingOwnerORM.update({
              where: this.buildingOwner.id,
              data: {
                email: this.emailToSubmit.email,
              },
            });
          });
        this.$store.state.showLoaderOverlay = false;
      }
    },
    navigateBack() {
      this.$router.push({ name: "meetings" });
    },
    compareProposedVotes(proposedVotes) {
      for (const proposedVote of proposedVotes) {
        for (const initialProposedVoted of this.initialProposedVotes) {
          if (
            proposedVote.meeting_resolution_id === initialProposedVoted.meeting_resolution_id &&
            proposedVote.majority_id === initialProposedVoted.majority_id
          ) {
            if (proposedVote.vote !== initialProposedVoted.vote) {
              this.initialProposedVotesWhereChanged = true;
              return;
            }
          }
        }
      }
      this.initialProposedVotesWhereChanged = false;
    },
  },
};
</script>
